import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { AddressService } from "@modules/common/components/address/address.service";
import { JobDto, ListResultDto, SearchDto } from "@shared/models";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ContactPipe, DateExPipe, getFullDayName, PublishSubscribe } from "../../common";
import { AppQuery, LoginStatus } from "../../common/app.store";
import { SortOrder, TableColumn } from "../../common/components";
import { AddressPipe } from "../../common/pipes/adddress.pipe";
import { JobService, ProductSettingService } from "../../common/services";
import { BaseJobList } from "../base-job-list";
import { ScheduleJobComponent } from "../schedule-job/schedule-job.component";

@Component({
  templateUrl: "./list-lite.component.html",
})
export class ListLiteComponent extends BaseJobList implements OnInit, OnDestroy {
  showNavigation = false;
  jobDay: string;
  owner = "";
  sortField = "!jobId";
  grouping = "";
  groupings = {
    ["0"]: "Logging",
    ["1"]: "Scheduling",
    ["4"]: "Parts",
    ["5"]: "Quotation",
    ["6"]: "Cancelled",
    ["7"]: "Completed",
    ["8"]: "Closed",
    ["9"]: "Escalation",
    ["z"]: "Jobs",
    ["x"]: "Errors"
  };

  isMobile: Observable<boolean>;

  get baseColumns(): TableColumn[] {
    return [
      { field: "contact", header: "Contact.Name", defaultSort: SortOrder.Ascending, pipe: new ContactPipe() },
      { field: "address", header: "Address.Line3", defaultSort: SortOrder.Ascending, pipe: [new AddressPipe(), "line34"] },
      { field: "resourceId", header: "Job.Resource", defaultSort: SortOrder.Ascending },
      { field: "statusId", header: "Job.StatusID", defaultSort: SortOrder.Ascending },
      { field: "typeId", header: "Job.TypeID", defaultSort: SortOrder.Ascending },
      { field: "nextAction", header: "JobSummary.Due", defaultSort: SortOrder.Descending, pipe: [new DateExPipe(), "MMM-dd"] },
      { field: "contact.mobile", header: "Contact.Mobile", defaultSort: SortOrder.Ascending },
      { field: "jobId", header: "Job.JobID", defaultSort: SortOrder.Ascending, class: "d-md-table-cell d-none",
      sortOrder: SortOrder.Descending },
      { field: "ourRef", header: "Job.OurReference", defaultSort: SortOrder.Ascending, class: "w-20", hidden: true },
      { field: "holdTypeId", header: "Job.HoldTypeID", defaultSort: SortOrder.Ascending, class: "w-15", hidden: true },
      { field: "errors.0.data", header: "Job.ErrorData", defaultSort: SortOrder.None, class: "w-15", hidden: true },
      { field: "errors.0.description", header: "Job.ErrorDescription", defaultSort: SortOrder.None, class: "w-20", hidden: true },

      { field: "", header: "Job.Schedule", defaultSort: SortOrder.None, hideHandset: true },
    ];
  }
  searchParams: any;
  userCanEdit: boolean;

  constructor(
    private modal: NgbModal,
    public route: ActivatedRoute,
    jobService: JobService,
    productSetting: ProductSettingService,
    private appQuery: AppQuery,
    private pubSub: PublishSubscribe,
    responsive: BreakpointObserver,
    protected address: AddressService
  ) {
    super(jobService, productSetting, address);
    this.isMobile = responsive.observe(Breakpoints.Handset).pipe(map(o => o.matches));
  }

  protected setVars(params: ParamMap): void {
    this.jobDay = params.get("jobday") ;
    this.showNavigation = false;
    this.searchParams = Object.assign(
      {},
      ...params.keys.filter(k => k !== "tenant").map((key) => ({[key]: params.get(key)}))
    );
    super.setVars(params);
  }

  ngOnInit() {
    super.ngOnInit();
    const roles = this.appQuery.roles;
    this.appQuery.$loginStatus.pipe(this.notDisposed()).subscribe(loginStatus =>
    this.userCanEdit = loginStatus === LoginStatus.True && roles.length && roles.some(r => r !== "VIEW"));
  }

  public displayName(jobDay: string): string {
    const includeWeekend = this.productSetting.booleanValue("JobIncludesWeekend");
    switch (jobDay) {
      case "Days1":
      case "Days2":
      case "Days3":
      case "Days4":
        return this.productSetting.stringValue("JobSummary" + jobDay);
      case "Yesterday":
        return getFullDayName(-1, includeWeekend);
      case "Tomorrow":
        return getFullDayName(1, includeWeekend);
      case "Tomorrow2":
        return getFullDayName(2,  includeWeekend);
      default:
        return jobDay;
    }
  }

  // @todo - see if this reduces changes triggers
  remoteData: (columns: TableColumn[]) => Promise<void> = (columns) => this.search(true, columns);

  setupColumns(): void {
    this.columns = [...this.baseColumns];
  }

  innerSearch(page: number, size: number, columns: TableColumn[]): Observable<ListResultDto<JobDto>> {
    const sortCol = columns.find(c => c.sortOrder > SortOrder.None);
    const jobSearch: SearchDto = {
      orderBy: [(sortCol.sortOrder === SortOrder.Descending ? "!" : "") + sortCol.field],
      pageSize: size,
      pageNumber: page,
      filters: {
        closed: "false",
        ...this.searchParams
      }
    };
    jobSearch.filters.jobday = this.jobDay;
    return this.jobService.queryJobs(jobSearch);
  }

  dateString(): string {
    return this.displayName(this.jobDay);
  }

  schedule(job: JobDto) {
    const modalRef = this.modal.open(ScheduleJobComponent, { size: "xl"});
    modalRef.componentInstance.job = job;
    modalRef.closed.subscribe((result: JobDto) => {
      if (result) {
        job = result;
      }
      this.search(false, this.columns);
    });
    return false;
  }

  showResource(resourceId: string): boolean {
    this.pubSub.publish("resource_modal", resourceId);
    return false;
  }
}
